/**
 * Energy Certificate Simulator - Main Component
 *
 * Wizard-based interface for calculating energy certificates.
 * Uses Mantine Stepper for multi-step form navigation.
 *
 * @license Proprietary - Commercial module
 */

import {
  Container,
  Paper,
  Stepper,
  Title,
  Text,
  Group,
  Button,
  Stack,
  Badge,
  Tooltip,
  ActionIcon,
  Box,
  Progress,
  ThemeIcon,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import {
  IconHome,
  IconWall,
  IconFlame,
  IconSolarPanel,
  IconChartBar,
  IconArrowRight,
  IconArrowLeft,
  IconRefresh,
  IconDeviceFloppy,
  IconInfoCircle,
  IconFileImport,
  IconFileExport,
} from '@tabler/icons-react';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSEO } from '../../../shared/hooks/useSEO';

import { useSimulatorForm, WIZARD_STEPS } from '../hooks/useSimulatorForm';
import { SimulatorErrorBoundary } from './shared/ErrorBoundary';
import { ImportModal } from './shared/ImportModal';
import { BuildingStep, InsulationStep, HeatingStep, RenewablesStep } from './forms';
import { ResultsView } from './results';
import { createExportData, downloadJSON } from '../utils/jsonExport';
import { STORAGE_KEYS } from '../types';
import {
  STORAGE_KEY_PRICES,
  STORAGE_KEY_INVESTMENT,
  STORAGE_KEY_SETTINGS,
  DEFAULT_SETTINGS,
} from '../heating-cost/hooks/useHeatingCostConfig';
import { DEFAULT_PREFERENCES } from '../hooks/useLocalStorage';
import { getDefaultFuelPrices, getDefaultInvestmentCosts } from '../heating-cost';
import type { WizardStep } from '../types';
import type { FuelPrice, InvestmentCostDefinition } from '../heating-cost';
import type { UserPreferences } from '../hooks/useLocalStorage';
import type { HeatingCostSettings } from '../heating-cost/hooks/useHeatingCostConfig';

// =============================================================================
// CONSTANTS
// =============================================================================

const STEP_CONFIG: Record<
  WizardStep,
  {
    label: string;
    description: string;
    icon: React.ReactNode;
  }
> = {
  building: {
    label: 'Budynek',
    description: 'Podstawowe parametry',
    icon: <IconHome size={18} />,
  },
  insulation: {
    label: 'Izolacja',
    description: 'Stan ocieplenia',
    icon: <IconWall size={18} />,
  },
  heating: {
    label: 'Ogrzewanie',
    description: 'System grzewczy',
    icon: <IconFlame size={18} />,
  },
  renewables: {
    label: 'OZE',
    description: 'Źródła odnawialne',
    icon: <IconSolarPanel size={18} />,
  },
  results: {
    label: 'Wyniki',
    description: 'Certyfikat',
    icon: <IconChartBar size={18} />,
  },
};

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

interface NavigationButtonsProps {
  currentStep: WizardStep;
  isStepValid: boolean;
  isOnResults: boolean;
  onPrevious: () => void;
  onNext: () => boolean;
  onRestart: () => void;
  onSave?: () => void;
}

// =============================================================================
// MOBILE PROGRESS BAR COMPONENT
// =============================================================================

interface MobileProgressBarProps {
  currentStepIndex: number;
  totalSteps: number;
  currentStep: WizardStep;
}

function MobileProgressBar({ currentStepIndex, totalSteps, currentStep }: MobileProgressBarProps) {
  const progress = ((currentStepIndex + 1) / totalSteps) * 100;
  const config = STEP_CONFIG[currentStep];

  return (
    <Stack gap="xs">
      <Group justify="space-between" align="center">
        <Group gap="xs">
          <ThemeIcon variant="light" size="sm" color="blue">
            {config.icon}
          </ThemeIcon>
          <Text fw={500} size="sm">
            {config.label}
          </Text>
        </Group>
        <Text size="sm" c="dimmed">
          {currentStepIndex + 1} / {totalSteps}
        </Text>
      </Group>
      <Progress value={progress} size="sm" radius="xl" aria-label="Postęp formularza" />
    </Stack>
  );
}

function NavigationButtons({
  currentStep,
  isStepValid,
  isOnResults,
  onPrevious,
  onNext,
  onRestart,
  onSave,
}: NavigationButtonsProps) {
  const isFirstStep = currentStep === 'building';

  if (isOnResults) {
    return (
      <Group justify="space-between" mt="xl">
        <Button variant="light" leftSection={<IconArrowLeft size={16} />} onClick={onPrevious}>
          Wróć do formularza
        </Button>
        <Group gap="sm">
          {onSave && (
            <Button variant="light" color="blue" leftSection={<IconDeviceFloppy size={16} />} onClick={onSave}>
              Zapisz wynik
            </Button>
          )}
          <Button variant="filled" color="green" leftSection={<IconRefresh size={16} />} onClick={onRestart}>
            Nowa symulacja
          </Button>
        </Group>
      </Group>
    );
  }

  return (
    <Group justify="space-between" mt="xl">
      <Button
        variant="light"
        leftSection={<IconArrowLeft size={16} />}
        onClick={onPrevious}
        disabled={isFirstStep}
      >
        Wstecz
      </Button>
      <Button
        variant="filled"
        rightSection={<IconArrowRight size={16} />}
        onClick={onNext}
        disabled={!isStepValid}
      >
        {currentStep === 'renewables' ? 'Oblicz certyfikat' : 'Dalej'}
      </Button>
    </Group>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export interface EnergyCertificateSimulatorProps {
  /** Whether to show the beta badge */
  showBetaBadge?: boolean;
  /** Callback when simulation is saved */
  onSave?: (id: string) => void;
}

/**
 * Energy Certificate Simulator
 *
 * A 5-step wizard for calculating building energy certificates:
 * 1. Building - basic parameters (area, period, type, floors)
 * 2. Insulation - wall, roof, window insulation
 * 3. Heating - system type, age, hot water source
 * 4. Renewables - PV and other renewable sources
 * 5. Results - EP indicator, energy class, recommendations
 */
export function EnergyCertificateSimulator({
  showBetaBadge = true,
  onSave,
}: EnergyCertificateSimulatorProps) {
  // React key reset: incrementing forces full remount (re-reads localStorage)
  const [resetKey, setResetKey] = useState(0);

  return (
    <SimulatorErrorBoundary>
      <SimulatorContent
        key={resetKey}
        showBetaBadge={showBetaBadge}
        onSave={onSave}
        onImportSuccess={() => setResetKey(k => k + 1)}
      />
    </SimulatorErrorBoundary>
  );
}

interface SimulatorContentProps extends EnergyCertificateSimulatorProps {
  onImportSuccess: () => void;
}

function SimulatorContent({
  showBetaBadge = true,
  onSave,
  onImportSuccess,
}: SimulatorContentProps) {
  const { osd } = useParams<{ osd: string }>();

  useSEO({
    title: 'Symulator Certyfikatu Energetycznego',
    description: 'Oblicz orientacyjny wskaźnik EP i klasę energetyczną budynku. Symulacja oparta na metodologii Dz.U. 2023 poz. 697.',
    canonicalPath: osd ? `/${osd}/symulator-certyfikatu` : '/symulator-certyfikatu',
  });

  const {
    form,
    currentStep,
    currentStepIndex,
    isOnResults,
    result,
    isStepValid,
    navigation,
    actions,
    totalSteps,
  } = useSimulatorForm();

  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [importModalOpened, setImportModalOpened] = useState(false);
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Handle step click in Stepper
  const handleStepClick = useCallback(
    (stepIndex: number) => {
      // Only allow clicking on completed or current steps
      if (stepIndex <= currentStepIndex) {
        const targetStep = WIZARD_STEPS[stepIndex];
        navigation.goToStep(targetStep);
      }
    },
    [currentStepIndex, navigation]
  );

  // Handle save
  const handleSave = useCallback(() => {
    const id = actions.saveResult();
    setSaveMessage('Zapisano symulację');
    setTimeout(() => setSaveMessage(null), 3000);
    onSave?.(id);
  }, [actions, onSave]);

  // Handle JSON export — reads all localStorage keys
  const handleExport = useCallback(() => {
    const formState = form.values;

    // Read saved simulations from localStorage
    let savedSimulations = [];
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.SAVED_SIMULATIONS);
      if (raw) savedSimulations = JSON.parse(raw);
    } catch { /* empty */ }

    // Read preferences
    let preferences: UserPreferences = DEFAULT_PREFERENCES;
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.PREFERENCES);
      if (raw) preferences = { ...DEFAULT_PREFERENCES, ...JSON.parse(raw) };
    } catch { /* empty */ }

    // Read heating cost config
    let fuelPrices: FuelPrice[] = getDefaultFuelPrices();
    try {
      const raw = localStorage.getItem(STORAGE_KEY_PRICES);
      if (raw) fuelPrices = JSON.parse(raw);
    } catch { /* empty */ }

    let investmentCosts: InvestmentCostDefinition[] = getDefaultInvestmentCosts();
    try {
      const raw = localStorage.getItem(STORAGE_KEY_INVESTMENT);
      if (raw) investmentCosts = JSON.parse(raw);
    } catch { /* empty */ }

    let settings: HeatingCostSettings = DEFAULT_SETTINGS;
    try {
      const raw = localStorage.getItem(STORAGE_KEY_SETTINGS);
      if (raw) settings = { ...DEFAULT_SETTINGS, ...JSON.parse(raw) };
    } catch { /* empty */ }

    const data = createExportData(
      formState,
      savedSimulations,
      preferences,
      fuelPrices,
      investmentCosts,
      settings
    );
    downloadJSON(data);
  }, [form.values]);

  // Render current step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 'building':
        return <BuildingStep form={form} />;
      case 'insulation':
        return <InsulationStep form={form} />;
      case 'heating':
        return <HeatingStep form={form} />;
      case 'renewables':
        return <RenewablesStep form={form} />;
      case 'results':
        return result ? (
          <ResultsView
            result={result}
            buildingArea={form.values.usableArea}
            onRestart={navigation.restart}
            heatingSystem={form.values.heatingSystem}
            systemAge={form.values.systemAge}
          />
        ) : null;
      default:
        return null;
    }
  };

  return (
      <Container size="md" py="xl">
        <Stack gap="xl">
          {/* Header */}
          <Box>
            <Group justify="space-between" align="flex-start">
              <div>
                <Group gap="sm" mb="xs">
                  <Title order={2}>Symulator Certyfikatu Energetycznego</Title>
                  {showBetaBadge && (
                    <Badge color="orange" variant="light" size="sm">
                      BETA
                    </Badge>
                  )}
                </Group>
                <Text c="dimmed" size="sm">
                  Oblicz orientacyjny wskaźnik EP i klasę energetyczną budynku
                </Text>
              </div>
              <Group gap="xs">
                <Tooltip label="Importuj konfigurację z pliku JSON">
                  <ActionIcon
                    variant="light"
                    color="blue"
                    onClick={() => setImportModalOpened(true)}
                    data-testid="import-button"
                  >
                    <IconFileImport size={18} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Eksportuj konfigurację do pliku JSON">
                  <ActionIcon
                    variant="light"
                    color="green"
                    onClick={handleExport}
                    data-testid="export-button"
                  >
                    <IconFileExport size={18} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Symulacja ma charakter orientacyjny i nie zastępuje oficjalnego świadectwa">
                  <ActionIcon variant="subtle" color="gray">
                    <IconInfoCircle size={20} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Group>
          </Box>

          {/* Import Modal */}
          <ImportModal
            opened={importModalOpened}
            onClose={() => setImportModalOpened(false)}
            onImportSuccess={onImportSuccess}
          />

          {/* Stepper / Mobile Progress Bar */}
          <Paper p="md" withBorder radius="md">
            {isMobile ? (
              <MobileProgressBar
                currentStepIndex={currentStepIndex}
                totalSteps={totalSteps + 1} // Include results step
                currentStep={currentStep}
              />
            ) : (
              <Stepper
                active={currentStepIndex}
                onStepClick={handleStepClick}
                allowNextStepsSelect={false}
                size="sm"
                iconSize={32}
              >
                {WIZARD_STEPS.map((step) => {
                  const config = STEP_CONFIG[step];
                  return (
                    <Stepper.Step
                      key={step}
                      label={config.label}
                      description={config.description}
                      icon={config.icon}
                      loading={step === currentStep && !isOnResults}
                    />
                  );
                })}
              </Stepper>
            )}
          </Paper>

          {/* Step content */}
          <Paper p="xl" withBorder radius="md" shadow="sm">
            {!isOnResults && (
              <Group justify="space-between" mb="lg">
                <Title order={4}>{STEP_CONFIG[currentStep].label}</Title>
                <Badge variant="light" color="gray">
                  Krok {currentStepIndex + 1} z {WIZARD_STEPS.length}
                </Badge>
              </Group>
            )}

            {renderStepContent()}

            <NavigationButtons
              currentStep={currentStep}
              isStepValid={isStepValid}
              isOnResults={isOnResults}
              onPrevious={navigation.goToPreviousStep}
              onNext={navigation.goToNextStep}
              onRestart={navigation.restart}
              onSave={result ? handleSave : undefined}
            />

            {saveMessage && (
              <Text ta="center" c="green" size="sm" mt="md">
                {saveMessage}
              </Text>
            )}
          </Paper>

          {/* Footer info */}
          <Text size="xs" c="dimmed" ta="center">
            Obliczenia oparte na metodologii Dz.U. 2023 poz. 697 i wymaganiach WT 2021.
            Dokładność szacowana na ~80%.
          </Text>
        </Stack>
      </Container>
  );
}

export default EnergyCertificateSimulator;
